/**
 * Product Info & Media Component
 * Handles variant selection, pricing updates, accordions, lightbox, and AJAX add-to-cart
 */

class ProductInfoAndMediaComponent extends HTMLElement {
  constructor() {
    super();

    /** @type {HTMLDialogElement | null} */
    this.lightbox = null;

    /** @type {HTMLElement | null} */
    this.lightboxMedia = null;

    /** @type {number} */
    this.currentLightboxIndex = 0;

    /** @type {NodeListOf<HTMLElement>} */
    this.mediaItems = this.querySelectorAll('.product-info-and-media__media-item');

    /** @type {AbortController} */
    this.controller = new AbortController();

    /** @type {MediaQueryList} */
    this.mobileMediaQuery = window.matchMedia('(max-width: 768px)');

    /** @type {any} */
    this.swiperInstance = null;

    /** @type {any} */
    this.gallerySwiperInstance = null;

    /** @type {() => void} */
    this._boundHandleBreakpoint = this.handleBreakpoint.bind(this);
  }

  connectedCallback() {
    const { signal } = this.controller;

    this.lightbox = this.querySelector('[ref="lightbox"]');
    this.lightboxMedia = this.querySelector('[ref="lightboxMedia"]');

    // Initialize all features
    this.initVariantSelection(signal);
    this.initAccordions(signal);
    if (this.dataset.currentVariantId) {
      this.updateVariantAccordionContent(this.dataset.currentVariantId);
    }
    // Covers every variant-change path that dispatches on (or within) this
    // component: same-product pills and the combined section's variant-picker.
    this.addEventListener('variant:change', (e) => {
      const variantId = /** @type {CustomEvent} */ (e).detail?.variantId;
      if (!variantId) return;
      this.dataset.currentVariantId = String(variantId);
      this.updateVariantAccordionContent(String(variantId));
    }, { signal });
    this.initLightbox(signal);
    this.initVideoControls(signal);
    this.initFormSubmit(signal);
    this.loadRelatedProducts();
    this.initRelatedSwiper();
    this.initGallerySwiper();
    this.initDragScroll(signal);

    this.mobileMediaQuery.addEventListener('change', this._boundHandleBreakpoint);
  }

  disconnectedCallback() {
    this.controller.abort();
    this.destroySwiper();
    this.destroyGallerySwiper();
    this.mobileMediaQuery.removeEventListener('change', this._boundHandleBreakpoint);
  }

  /**
   * Handle breakpoint changes for mobile Swiper
   */
  handleBreakpoint() {
    if (this.mobileMediaQuery.matches) {
      this.initRelatedSwiper();
      this.initGallerySwiper();
    } else {
      this.destroySwiper();
      this.destroyGallerySwiper();
    }
  }

  /**
   * Initialize Swiper for related products (mobile only)
   */
  initRelatedSwiper() {
    // Only initialize on mobile
    if (!this.mobileMediaQuery.matches) return;
    if (typeof /** @type {any} */ (window).Swiper === 'undefined') return;

    this.destroySwiper();

    const swiperEl = this.querySelector('[ref="relatedSwiper"]');
    const paginationEl = this.querySelector('.product-info-and-media__pagination');

    if (!swiperEl) return;

    const SwiperConstructor = /** @type {any} */ (window).Swiper;
    this.swiperInstance = new SwiperConstructor(/** @type {HTMLElement} */ (swiperEl), {
      slidesPerView: 'auto',
      spaceBetween: 8,
      grabCursor: true,
      pagination: paginationEl ? {
        el: /** @type {HTMLElement} */ (paginationEl),
        clickable: true,
        bulletClass: 'swiper-pagination-bullet',
        bulletActiveClass: 'swiper-pagination-bullet-active',
      } : false,
    });
  }

  /**
   * Destroy Swiper instance
   */
  destroySwiper() {
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }

  /**
   * Initialize Swiper for gallery (mobile only)
   */
  initGallerySwiper() {
    if (!this.mobileMediaQuery.matches) return;
    if (typeof /** @type {any} */ (window).Swiper === 'undefined') return;

    this.destroyGallerySwiper();

    const swiperEl = this.querySelector('[ref="gallerySwiper"]');

    if (!swiperEl) return;

    const SwiperConstructor = /** @type {any} */ (window).Swiper;
    this.gallerySwiperInstance = new SwiperConstructor(/** @type {HTMLElement} */ (swiperEl), {
      slidesPerView: 'auto',
      spaceBetween: 8,
      centeredSlides: true,
      grabCursor: true,
      on: {
        click: (swiper) => {
          const idx = swiper.clickedIndex;
          if (idx != null && !Number.isNaN(idx)) {
            this.openLightbox(idx);
          }
        },
      },
    });
  }

  /**
   * Destroy gallery Swiper instance
   */
  destroyGallerySwiper() {
    if (this.gallerySwiperInstance) {
      this.gallerySwiperInstance.destroy(true, true);
      this.gallerySwiperInstance = null;
    }
  }

  /**
   * Initialize drag-to-scroll for desktop related products
   * @param {AbortSignal} signal
   */
  initDragScroll(signal) {
    const scrollContainer = this.querySelector('.product-info-and-media__related-scroll');
    if (!scrollContainer) return;

    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;
    let hasDragged = false;

    scrollContainer.addEventListener('mousedown', (e) => {
      // Only on desktop (not mobile)
      if (this.mobileMediaQuery.matches) return;
      
      isDown = true;
      hasDragged = false;
      scrollContainer.classList.add('is-dragging');
      startX = /** @type {MouseEvent} */ (e).clientX;
      scrollLeft = scrollContainer.scrollLeft;
    }, { signal });

    scrollContainer.addEventListener('mouseleave', () => {
      isDown = false;
      scrollContainer.classList.remove('is-dragging');
    }, { signal });

    scrollContainer.addEventListener('mouseup', () => {
      isDown = false;
      scrollContainer.classList.remove('is-dragging');
    }, { signal });

    scrollContainer.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = /** @type {MouseEvent} */ (e).clientX;
      const walk = (x - startX) * 1.2; // Scroll speed multiplier
      
      // Mark as dragged if moved more than 5px
      if (Math.abs(walk) > 5) {
        hasDragged = true;
      }
      
      scrollContainer.scrollLeft = scrollLeft - walk;
    }, { signal });

    // Prevent link clicks when dragging
    scrollContainer.addEventListener('click', (e) => {
      if (hasDragged) {
        e.preventDefault();
        e.stopPropagation();
        hasDragged = false;
      }
    }, { signal, capture: true });
  }

  /**
   * Initialize variant selection
   * @param {AbortSignal} signal
   */
  initVariantSelection(signal) {
    const variantPills = this.querySelectorAll('.product-info-and-media__variant-pill');

    variantPills.forEach((pill) => {
      pill.addEventListener('click', (e) => {
        const label = /** @type {HTMLLabelElement} */ (e.currentTarget);
        const input = label.querySelector('input');

        if (!input || input.disabled) return;
        if (label.classList.contains('product-info-and-media__variant-pill--selected')) return;

        // Update variant description immediately on click, even for combined listings.
        // The variant-picker morph will confirm/update it after the async fetch.
        this.updateVariantDescription(label);

        // Combined Listings: the variant-picker handles navigation. Trigger the loading
        // state for all cases — same-product variants (plan-selector refresh) and
        // navigational pills (full main morph) both benefit from the visual feedback.
        if (label.closest('variant-picker')) {
          this.setVariantLoading(true);
          return;
        }

        this.setVariantLoading(true);

        // Update selected state
        variantPills.forEach((p) => p.classList.remove('product-info-and-media__variant-pill--selected'));
        label.classList.add('product-info-and-media__variant-pill--selected');

        // Update prices
        this.updatePrices(label);

        // Update variant description
        this.updateVariantDescription(label);

        // Update URL without reload
        this.updateURL(input.value);

        // Update variant ID attribute
        this.dataset.currentVariantId = input.value;

        // Dispatch variant update event
        this.dispatchEvent(new CustomEvent('variant:change', {
          bubbles: true,
          detail: {
            variantId: input.value,
            price: label.dataset.variantPrice,
            comparePrice: label.dataset.variantComparePrice,
            available: label.dataset.variantAvailable === 'true'
          }
        }));
      }, { signal });
    });
  }

  /**
   * Toggle a transient loading state during variant changes.
   * Locks pointer events and dims the details column while async listeners
   * (e.g. subscription block re-fetch) settle, preventing premature ATC.
   * @param {boolean} isLoading
   */
  setVariantLoading(isLoading) {
    if (this._variantLoadingTimer) {
      clearTimeout(this._variantLoadingTimer);
      this._variantLoadingTimer = null;
    }
    if (isLoading) {
      this.classList.add('is-variant-loading');
      this._variantLoadingTimer = setTimeout(() => {
        this.classList.remove('is-variant-loading');
        this._variantLoadingTimer = null;
      }, 600);
    } else {
      this.classList.remove('is-variant-loading');
    }
  }

  /**
   * Update prices based on selected variant
   * @param {HTMLElement} selectedPill
   */
  updatePrices(selectedPill) {
    const price = parseInt(selectedPill.dataset.variantPrice || '0', 10);
    const comparePrice = parseInt(selectedPill.dataset.variantComparePrice || '0', 10);
    const available = selectedPill.dataset.variantAvailable === 'true';

    const priceEl = this.querySelector('[ref="price"]');
    const comparePriceEl = /** @type {HTMLElement | null} */ (this.querySelector('[ref="comparePrice"]'));
    const atcPriceEl = this.querySelector('[ref="atcPrice"]');
    const discountBadge = /** @type {HTMLElement | null} */ (this.querySelector('[ref="discountBadge"]'));
    const atcButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="addToCartButton"]'));
    const atcLabel = this.querySelector('.product-info-and-media__atc-label');

    // Format price
    const formattedPrice = this.formatMoney(price);
    const formattedCompare = comparePrice ? this.formatMoney(comparePrice) : '';

    if (priceEl) priceEl.textContent = formattedPrice;
    if (atcPriceEl) atcPriceEl.textContent = formattedPrice;

    // Handle compare price
    if (comparePriceEl) {
      if (comparePrice > price) {
        comparePriceEl.textContent = formattedCompare;
        comparePriceEl.style.display = '';
      } else {
        comparePriceEl.style.display = 'none';
      }
    }

    // Handle discount badge
    if (discountBadge) {
      if (comparePrice > price) {
        const savingsPercent = Math.round(((comparePrice - price) / comparePrice) * 100);
        const saveText = discountBadge.textContent?.split(' ')[0] || 'Save';
        discountBadge.textContent = `${saveText} ${savingsPercent}%`;
        discountBadge.style.display = '';
      } else {
        discountBadge.style.display = 'none';
      }
    }

    // Update button state
    if (atcButton) {
      atcButton.disabled = !available;
    }

    if (atcLabel) {
      if (available) {
        atcLabel.textContent = 'ADD TO CART';
      } else {
        atcLabel.textContent = 'SOLD OUT';
      }
    }
  }

  /**
   * Update variant description text below the variant pills.
   * @param {HTMLElement} selectedPill
   */
  updateVariantDescription(selectedPill) {
    const display = /** @type {HTMLElement | null} */ (this.querySelector('[data-variant-description-display]'));
    if (!display) return;
    const text = (selectedPill.dataset.variantDescription || '').trim();
    display.textContent = text;
    if (text === '') {
      display.setAttribute('hidden', '');
    } else {
      display.removeAttribute('hidden');
    }
  }

  /**
   * Show the accordion content matching the selected variant.
   * Accordions with a Variant Metafield Key render one content div per
   * variant; hide the whole accordion when the active content is empty.
   * @param {string} variantId
   */
  updateVariantAccordionContent(variantId) {
    const groups = this.querySelectorAll('[data-variant-accordion-content]');
    if (!groups.length) return;

    // Ignore ids the accordions don't know (e.g. a plan-selector pointed at a
    // different product) — hiding every div would blank all accordions.
    const id = String(variantId);
    if (!Array.from(groups).some((el) => el.dataset.variantId === id)) return;

    groups.forEach((el) => {
      el.hidden = el.dataset.variantId !== id;
    });

    this.querySelectorAll('accordion-custom').forEach((accordion) => {
      const active = accordion.querySelector('[data-variant-accordion-content]:not([hidden])');
      if (!active) return;
      const isEmpty = active.textContent?.trim() === '' && !active.querySelector('img, video, iframe');
      accordion.hidden = isEmpty;
    });
  }

  /**
   * Format money from cents to currency string
   * @param {number} cents
   * @returns {string}
   */
  formatMoney(cents) {
    // Use Shopify's money format if available
    // @ts-ignore - Shopify global may have formatMoney
    if (window.Shopify?.formatMoney) {
      // @ts-ignore
      return window.Shopify.formatMoney(cents);
    }
    // Fallback to basic USD formatting
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Update browser URL with variant
   * @param {string} variantId
   */
  updateURL(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  }

  /**
   * Initialize exclusive accordion behavior
   * @param {AbortSignal} signal
   */
  initAccordions(signal) {
    const accordions = this.querySelectorAll('.product-info-and-media__accordion');

    accordions.forEach((accordion) => {
      const details = accordion.querySelector('details');
      if (!details) return;

      details.addEventListener('toggle', (e) => {
        if (!details.open) return;

        // Close other accordions
        accordions.forEach((otherAccordion) => {
          if (otherAccordion === accordion) return;
          const otherDetails = otherAccordion.querySelector('details');
          if (otherDetails && otherDetails.open) {
            otherDetails.open = false;
          }
        });
      }, { signal });
    });
  }

  /**
   * Initialize lightbox functionality
   * @param {AbortSignal} signal
   */
  initLightbox(signal) {
    if (!this.lightbox || !this.lightboxMedia) return;

    // Open lightbox buttons
    const openButtons = this.querySelectorAll('[data-open-lightbox]');
    openButtons.forEach((btn, index) => {
      btn.addEventListener('click', () => {
        this.openLightbox(index);
      }, { signal });
    });

    // Close button
    const closeBtn = this.querySelector('[ref="lightboxClose"]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.closeLightbox();
      }, { signal });
    }

    // Close on backdrop click
    this.lightbox.addEventListener('click', (e) => {
      if (e.target === this.lightbox) {
        this.closeLightbox();
      }
    }, { signal });

    // Navigation
    const prevBtn = this.querySelector('[ref="lightboxPrev"]');
    const nextBtn = this.querySelector('[ref="lightboxNext"]');

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        this.navigateLightbox(-1);
      }, { signal });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        this.navigateLightbox(1);
      }, { signal });
    }

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!this.lightbox?.open) return;

      if (e.key === 'Escape') {
        this.closeLightbox();
      } else if (e.key === 'ArrowLeft') {
        this.navigateLightbox(-1);
      } else if (e.key === 'ArrowRight') {
        this.navigateLightbox(1);
      }
    }, { signal });
  }

  /**
   * Open lightbox at specific index
   * @param {number} index
   */
  openLightbox(index) {
    if (!this.lightbox || !this.lightboxMedia) return;

    this.currentLightboxIndex = index;
    this.updateLightboxMedia();
    this.lightbox.showModal();
    document.body.style.overflow = 'hidden';
  }

  /**
   * Close lightbox
   */
  closeLightbox() {
    if (!this.lightbox) return;

    this.lightbox.close();
    document.body.style.overflow = '';
  }

  /**
   * Navigate lightbox
   * @param {number} direction - -1 for prev, 1 for next
   */
  navigateLightbox(direction) {
    const imageItems = this.querySelectorAll('.product-info-and-media__image-btn');
    const total = imageItems.length;
    if (total === 0) return;

    this.currentLightboxIndex = (this.currentLightboxIndex + direction + total) % total;
    this.updateLightboxMedia();
  }

  /**
   * Update lightbox media content
   */
  updateLightboxMedia() {
    if (!this.lightboxMedia) return;

    const imageButtons = this.querySelectorAll('.product-info-and-media__image-btn');
    const currentBtn = imageButtons[this.currentLightboxIndex];
    if (!currentBtn) return;

    const img = currentBtn.querySelector('img');
    if (!img) return;

    // Get high-res version
    const srcset = img.srcset || img.src;
    const highResSrc = srcset.split(',').pop()?.trim().split(' ')[0] || img.src;

    this.lightboxMedia.innerHTML = `<img src="${highResSrc}" alt="${img.alt || ''}" loading="eager">`;
  }

  /**
   * Initialize video sound controls
   * @param {AbortSignal} signal
   */
  initVideoControls(signal) {
    const videos = /** @type {NodeListOf<HTMLVideoElement>} */ (this.querySelectorAll('.product-info-and-media__video'));

    videos.forEach((video) => {
      const wrapper = video.closest('.product-info-and-media__video-wrapper');
      const soundBtn = wrapper?.querySelector('.product-info-and-media__video-sound-btn');

      if (!soundBtn) return;

      soundBtn.addEventListener('click', () => {
        if (video.muted) {
          video.muted = false;
          video.dataset.muted = 'false';
        } else {
          video.muted = true;
          video.dataset.muted = 'true';
        }
      }, { signal });
    });
  }

  /**
   * Initialize form submission (AJAX add to cart)
   * @param {AbortSignal} signal
   */
  initFormSubmit(signal) {
    const form = this.querySelector('form');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const addToCartBtn = /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="addToCartButton"]'));
      if (!addToCartBtn || addToCartBtn.disabled) return;

      // Disable button
      addToCartBtn.disabled = true;

      try {
        const formData = new FormData(form);
        // @ts-ignore - Shopify routes global
        const cartAddUrl = (window.Shopify?.routes?.root || '/') + 'cart/add.js';

        const response = await fetch(cartAddUrl, {
          method: 'POST',
          body: formData
        });

        const result = await response.json();

        if (response.ok) {
          // Success - show checkmark animation
          addToCartBtn.dataset.added = 'true';

          // Dispatch cart update event — triggers cart drawer open + cart icon update
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: result,
              sourceId: 'product-info-and-media',
              data: {
                source: 'product-form-component',
                itemCount: 1,
              }
            }
          }));

          // Update cart count
          this.updateCartCount();

          // Reset button after animation
          setTimeout(() => {
            addToCartBtn.dataset.added = 'false';
            addToCartBtn.disabled = false;
          }, 2000);
        } else {
          // Error
          this.showError(result.description || result.message || 'Error adding to cart');
          addToCartBtn.disabled = false;
        }
      } catch (error) {
        console.error('Add to cart error:', error);
        this.showError('Error adding to cart');
        addToCartBtn.disabled = false;
      }
    }, { signal });
  }

  /**
   * Show error message
   * @param {string} message
   */
  showError(message) {
    const errorEl = this.querySelector('[ref="addToCartTextError"]');
    if (!errorEl) return;

    // Remove existing text nodes (keep the icon)
    const textNode = errorEl.childNodes[1];
    if (textNode) {
      textNode.textContent = ' ' + message;
    } else {
      errorEl.appendChild(document.createTextNode(' ' + message));
    }

    errorEl.classList.remove('hidden');

    setTimeout(() => {
      errorEl.classList.add('hidden');
    }, 5000);
  }

  /**
   * Update cart count in header
   */
  async updateCartCount() {
    try {
      // @ts-ignore - Shopify routes global
      const cartUrl = (window.Shopify?.routes?.root || '/') + 'cart.js';
      const response = await fetch(cartUrl);
      const cart = await response.json();

      // Try to update cart icon count
      const cartCounts = document.querySelectorAll('.cart-count, [data-cart-count]');
      cartCounts.forEach((el) => {
        el.textContent = cart.item_count.toString();
        if (cart.item_count > 0) {
          el.classList.remove('hidden');
        }
      });

      // Dispatch cart:update so cart icon count stays in sync
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'product-info-and-media',
          data: {
            source: 'product-form-component',
            itemCount: cart.item_count,
          }
        }
      }));
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  /**
   * Load related products via Shopify recommendations API (fallback)
   */
  async loadRelatedProducts() {
    const placeholder = /** @type {HTMLElement | null} */ (this.querySelector('.product-info-and-media__recommendations-placeholder'));
    if (!placeholder) return;

    const url = placeholder.dataset.url;
    if (!url) return;

    try {
      const response = await fetch(url);
      const html = await response.text();

      // Parse and extract product cards
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const cards = doc.querySelectorAll('.component-quick-add-card');

      if (cards.length > 0) {
        const scrollContainer = placeholder.closest('.product-info-and-media__related-scroll');
        if (scrollContainer) {
          placeholder.remove();
          cards.forEach((card) => {
            scrollContainer.appendChild(card.cloneNode(true));
          });
        }
      }
    } catch (error) {
      console.error('Error loading recommendations:', error);
    }
  }
}

// Register the custom element
if (!customElements.get('product-info-and-media-component')) {
  customElements.define('product-info-and-media-component', ProductInfoAndMediaComponent);
}
